import { type PartState, PartStates } from "./part-state.js";
import { SheetData } from "./sheet-data.js";
import {
  counted,
  pageFetch,
  type SheetKind,
  sheetTitle,
  type SheetToolProperties,
  startSheetTool,
} from "./sheet-tool.js";
import {
  type StructureFile,
  structureFile,
  structureItems,
  type StructurePart,
  StructureSheet,
} from "./structure-sheet.js";
import { GlyphLibrary } from "./worksheet-drawing.js";
import { sheetOrder } from "./worksheet-seed.js";
import { sheetCharacters } from "./worksheet-words.js";

/**
 * The character structure worksheet generator: the 结构, 部首 and 拆字 sheet
 * as a kind of sheet `sheet-tool.ts` draws.
 */

/** The most characters one sheet takes. */
export const structureLimit = 40;

/** The parts, in the order they print, and whether each prints by default. */
export const structureStates = new PartStates<StructurePart>([
  ["structure", true],
  ["radical", true],
  ["parts", true],
  ["key", true],
]);

/** The structure sheet, drawn from its data file. */
export function structureSheetKind(
  data: SheetData<StructureFile>,
): SheetKind<PartState<StructurePart>> {
  return {
    read: (hash) => structureStates.read(hash),
    fragment: (state) => structureStates.fragment(state),
    nothingAsked: (state) =>
      state.structure || state.radical || state.parts
        ? undefined
        : "Tick at least one exercise under Include.",
    title: (state, list) =>
      sheetTitle(state, list, {
        typed: "Character structure",
        saved: "Character structure from my saved words",
      }),

    draw: async ({ state, words, labels }) => {
      const file = await data.load();
      if (file === undefined) {
        return "Could not load the worksheet data. Try again in a moment.";
      }
      const characters = sheetCharacters(words, structureLimit * 4).map(
        (character) => character.character,
      );
      const { items, missing } = structureItems(
        file,
        characters,
        state,
        structureLimit,
      );
      const left =
        missing.length === 0
          ? ""
          : ` Nothing to ask about ${missing.slice(0, 20).join(" ")}${missing.length > 20 ? " and others" : ""}.`;
      if (items.length === 0) {
        return `None of these characters can be asked about.${left}`;
      }

      const library = new GlyphLibrary({ document });
      const pages = new StructureSheet({ library }).pages(
        state,
        sheetOrder(items, state),
        file.structures,
        labels,
      );

      return {
        nodes: [library.element, ...pages],
        status: `${counted(items.length, "character", "characters")} on ${counted(pages.length, "page", "pages")}.${left}`,
      };
    },
  };
}

/** Start the structure worksheet generator on its container. */
export function startStructureWorksheetTool(
  container: HTMLElement,
  properties: SheetToolProperties = pageFetch,
): void {
  const data = new SheetData({
    fetch: properties.fetch,
    address: container.dataset["cbData"] ?? "data.json",
    read: structureFile,
  });
  startSheetTool(container, structureSheetKind(data), properties);
}
